import React, { useEffect, useRef, useContext, useState, useMemo } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import { ChevronLeftIcon } from '@heroicons/react/solid';
import { Tooltip } from 'react-tooltip';
import { DocumentTextIcon, QuestionMarkCircleIcon, CheckCircleIcon } from '@heroicons/react/solid';
import SelectionOptions from './../9_postAssessmentDiscussion/components/SelectionOptions';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import { auth } from '../../../firebase';
import { functions } from '../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { serverTimestamp } from 'firebase/firestore';
import AutoResizingTextArea from '../../../components/AutoResizingTextArea';

export default function QBCheckForm() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { patientDataModel, updatePatientFirestore } = useContext(PatientDataContext);
  const [formState, setFormState] = useState({});

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Function to check if the form is valid
  const isFormValid = () => {
    // Check if all fields have values
    return Object.entries(formState).every(([fieldName, fieldData]) => {
      if (Array.isArray(fieldData.value)) {
        // For arrays (e.g., selection options, image uploads), check if there's at least one item
        return fieldData.value.length > 0;
      } else {
        // For strings (e.g., free text, date), check if there's a non-empty value
        return fieldData.value.trim() !== '';
      }
    });
  };

  // Add these handler functions near your other handler functions
  const handleFinish = async () => {
    setIsSaving(true);    
    setIsSaving(false);
    setIsDialogOpen(true);
  };

  // Update the handleGenerateReport function with debugging
const handleGenerateReport = async () => {
    setIsDialogOpen(false);
    setIsGenerating(true);
    
    // Immediately flag processing so the Documents dashboard can show "Finalising..."
    // Status + startedAt must be written together (same contract as TriggerRenderDocTemplate)
    // so DocumentsDashboard self-healing does not reset to idle on the next snapshot.
    try {
      await updatePatientFirestore(patientId, {
        '6_manageReports': {
          qbCheckReportProcessingStatus: 'processing',
          qbCheckReportProcessingStartedAt: serverTimestamp(),
        }
      });
    } catch (e) {
      console.error('Failed to set qbCheck processing status to processing:', e);
    }

    // List of all possible image fields that match the server-side expectations
    const imageFieldNames = [
      'qbCheckResultsImage', 
      'qbCheckActivityGraphPatientImage',
      'qbCheckActivityGraphSampleImage',
      'qbCheckAttentionAndImpulseControlGraphPatientImage',
      'qbCheckAttentionAndImpulseControlGraphSampleImage',
      'qbCheckObjectiveMeasuresImage'
    ];
    
    // Log the current state
    console.log("Generating report with formState:", 
      formState?.qbCheckResultsImage?.imageData ? 
      `Main image data present (length: ${formState.qbCheckResultsImage.imageData.length})` : 
      "No main image data");
    
    try {
      // Prepare the payload with all available images
      const payload = {
        patientId,
        clinicianId: auth.currentUser?.uid,
      };
      
      // Add any available images to the payload
      imageFieldNames.forEach(fieldName => {
        if (formState[fieldName]?.imageData) {
          console.log(`Including ${fieldName} in payload (length: ${formState[fieldName].imageData.length})`);
          payload[fieldName] = formState[fieldName].imageData;
        }
      });
      
      // Call Cloud Function to generate the report
      const generateReportFunction = httpsCallable(functions, 'generateQbCheckReport');
      const result = await generateReportFunction(payload);
      
      console.log('Report generated successfully:', result.data);
      // The rest of your code...
    } catch (error) {
      console.error('Error generating report:', error);
      setIsGenerating(false);
      // Show error message
    }
  };

  const handleCancelGeneration = () => {
    setIsGenerating(false);
  };

  const handleNextPageButtonClick = async () => {
    // Server flips status to complete and sets generated flags; we only navigate.
    navigate(`/patient/${patientId}/documents`);
  };


  // Constants
  const currentSectionNumber = 1;
  const currentSectionTitle = 'QbCheck';
  const prevSectionLink = '/documents';
  const prevSectionTitle = 'Documents';

  // Get the section data using useMemo
  const sectionData = useMemo(() => {
    return patientDataModel?.dataModel?.['6_manageReports']?.['qbCheck']?.[currentSectionNumber] || {};
  }, [patientDataModel]);

  // Get subsections data
  const subsections = useMemo(() => {
    return sectionData?.subsections || {};
  }, [sectionData]);

  // Add a ref to track initial load
  const isInitialLoad = useRef(true);  

  // ... existing code ...

// Initialize form state from Firestore data
useEffect(() => {
    // Only run on initial load
    if (!isInitialLoad.current) return;
    if (!sectionData?.subsections) return;
  
    const initialFormState = {};
  
    Object.entries(sectionData.subsections).forEach(([subsectionId, subsection]) => {
      // Initialize form state for questions
      if (subsection?.questions) {
        Object.entries(subsection.questions).forEach(([questionId, question]) => {
          if (question?.firestoreFieldName) {
            // Find this field's existing data in Firestore
            const firestoreField = patientDataModel?.dataModel?.['6_manageReports']?.['qbCheck']?.[currentSectionNumber]?.[question.firestoreFieldName]?.answer;
            
            // Create appropriate initial state based on subsection type
            if (subsection.subsectionType === 'verticalOptions') {
              initialFormState[question.firestoreFieldName] = {
                value: firestoreField?.value || [],
                otherValue: firestoreField?.otherValue || ''
              };
            } else if (subsection.subsectionType === 'freeText') {
              initialFormState[question.firestoreFieldName] = {
                value: firestoreField?.value || ''
              };
            } else if (subsection.subsectionType === 'imageUpload') {
              initialFormState[question.firestoreFieldName] = {
                value: firestoreField?.value || []
              };
            } else if (subsection.subsectionType === 'date') {
                initialFormState[question.firestoreFieldName] = {
                  value: firestoreField?.value || ''
                };
              }
          }
        });
      }
    });
  
    setFormState(initialFormState);
  
    // Mark initial load as complete
    isInitialLoad.current = false;
  }, [sectionData, patientDataModel]); 
  
  // ... existing code ...
  
  // Function to handle vertical selection changes
const handleSelect = async (fieldName, selectedOption, isSelected, question, isNoneOption = false) => {
  try {
    // Get current selections
    const currentSelections = formState[fieldName]?.value || [];
    let newSelections;

    if (question.questionType === 'selectOne') {
      // For selectOne, only allow one option at a time
      newSelections = isSelected ? [] : [selectedOption];
    } else {
      // For selectMultiple
      if (isNoneOption) {
        // When selecting a none option, clear all other selections
        newSelections = isSelected ? [] : [selectedOption];
      } else {
        if (isSelected) {
          // Remove the option if it's already selected
          newSelections = currentSelections.filter(option => option !== selectedOption);
        } else {
          // Add the new option and remove any none options
          newSelections = [...currentSelections.filter(opt =>
            !question.noneOptions?.includes(opt)
          ), selectedOption];
        }
      }
    }

    // Check if this field is a parent field that affects other fields' visibility
    const dependentFields = [];
    
    // Identify any dependent fields by checking all questions in all subsections
    Object.entries(subsections).forEach(([subsectionId, subsection]) => {
      Object.entries(subsection?.questions || {}).forEach(([questionId, q]) => {
        // Check if this question has conditions that depend on the field we're changing
        if (q.conditionsToShow && Object.values(q.conditionsToShow).some(
          condition => condition.firestoreFieldName === fieldName
        )) {
          dependentFields.push({
            fieldName: q.firestoreFieldName,
            subsectionId,
            questionId
          });
        }
      });
    });

    // Create update object
    const updateObject = {
      value: newSelections,
      otherValue: formState[fieldName]?.otherValue || ''
    };

    // Update local state first
    setFormState(prev => ({
      ...prev,
      [fieldName]: updateObject
    }));

    // Find the subsectionId and questionId for this field
    let targetSubsectionId, targetQuestionId;

    // Loop through subsections to find the question with matching firestoreFieldName
    Object.entries(subsections).forEach(([subsectionId, subsection]) => {
      Object.entries(subsection?.questions || {}).forEach(([questionId, q]) => {
        if (q.firestoreFieldName === fieldName) {
          targetSubsectionId = subsectionId;
          targetQuestionId = questionId;
        }
      });
    });

    if (!targetSubsectionId || !targetQuestionId) {
      throw new Error(`Question not found for field: ${fieldName}`);
    }

    // Create the nested update path for this specific question
    let updates = {
      [`6_manageReports.qbCheck.${currentSectionNumber}.${fieldName}.answer`]: updateObject
    };    

    // Update Firestore with all changes (the original field and any dependent fields)
    await updatePatientFirestore(patientId, updates);
  } catch (error) {
    console.error('Error updating Firestore:', error);
    // Revert local state if Firestore update fails
    setFormState(prev => ({
      ...prev,
      [fieldName]: {
        value: formState[fieldName]?.value || [],
        otherValue: formState[fieldName]?.otherValue || ''
      }
    }));
  }
};

  // Function to handle changes in the "Other" text field
const handleOtherTextChange = (fieldName, text) => {
  const updateObject = {
    value: formState[fieldName]?.value || [],
    otherValue: text || ''  // Default to empty string
  };

  setFormState(prevState => ({
    ...prevState,
    [fieldName]: updateObject
  }));
};

// Function to handle blur event on the "Other" text field
const handleOtherTextBlur = (fieldName) => {
  const currentValue = formState[fieldName];
  // Ensure we have valid values before updating Firestore
  const updateObject = {
    value: currentValue?.value || [],
    otherValue: currentValue?.otherValue || ''  // Default to empty string
  };

  // Find the subsectionId and questionId for this field
  let targetSubsectionId, targetQuestionId;

  // Loop through subsections to find the question with matching firestoreFieldName
  Object.entries(subsections).forEach(([subsectionId, subsection]) => {
    Object.entries(subsection?.questions || {}).forEach(([questionId, q]) => {
      if (q.firestoreFieldName === fieldName) {
        targetSubsectionId = subsectionId;
        targetQuestionId = questionId;
      }
    });
  });

  if (!targetSubsectionId || !targetQuestionId) {
    console.error(`Question not found for field: ${fieldName}`);
    return;
  }

  // Create the nested update path for this specific question
  const updates = {
    [`6_manageReports.qbCheck.${currentSectionNumber}.${fieldName}.answer`]: updateObject
  };

  // Update Firestore with the path structure matching our database
  updatePatientFirestore(patientId, updates);
};  


// Function to handle date change
const handleDateChange = (fieldName, value) => {
  setFormState(prevState => ({
    ...prevState,
    [fieldName]: {
      value: value
    }
  }));

  const updates = {
    [`6_manageReports.qbCheck.${currentSectionNumber}.${fieldName}.answer`]: {
      value: value
    }
  };

  updatePatientFirestore(patientId, updates);
};

  const [imageFiles, setImageFiles] = useState({});
// const [isUploading, setIsUploading] = useState(false);
const fileInputRefs = useRef({});


  // Updated function to handle free text input changes
const handleFreeTextChange = (fieldName, value, questionType) => {
    // Validate based on question type
    if (questionType === 'freeTextNumerical') {
      // Allow numbers with optional decimal points
      if (value !== '' && !/^-?\d*\.?\d*$/.test(value)) return;
    } else if (questionType === 'freeTextPercentage') {
      // Only allow whole numbers 0-100 (no decimals)
      if (value !== '' && (!/^\d*$/.test(value) || parseInt(value) > 100)) return;
    }
  
    // For freeText, just store the string value directly
    setFormState(prevState => ({
      ...prevState,
      [fieldName]: {
        value: value  // Store the string value directly in value
      }
    }));
  };
  
  // Updated function to handle free text input blur
  const handleFreeTextBlur = (fieldName, subsectionId, questionId) => {
    const currentValue = formState[fieldName]?.value || '';
    
    // Create the nested update path for this specific question
    const updates = {
      [`6_manageReports.qbCheck.${currentSectionNumber}.${fieldName}.answer`]: {
        value: currentValue
      }
    };
  
    updatePatientFirestore(patientId, updates);
  };

 // Modified function to handle image upload - storing only the URL
 const handleImageUpload = async (event, fieldName, subsectionId, questionId) => {
    const file = event.target.files[0];
    if (!file) return;
  
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/tiff', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a valid image file (.jpg, .png, .tiff, .webp)');
      return;
    }
  
    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert('Please upload an image smaller than 5MB');
      return;
    }
  
    setImageFiles(prev => ({
      ...prev,
      [fieldName]: {
        file,
        uploading: true
      }
    }));
  
    try {
      // Convert file to Base64
      const base64Data = await convertFileToBase64(file);
      console.log(`Base64 data generated, length: ${base64Data.length}`);
      
      // Store the Base64 data in formState
      const updateObject = {
        value: [URL.createObjectURL(file)], // For UI display only
        imageData: base64Data // This will be sent to the Cloud Function
      };
      
      console.log(`Setting formState for ${fieldName} with imageData`);
      
      // Update local state immediately
      setFormState(prevState => ({
        ...prevState,
        [fieldName]: updateObject
      }));     
  
      // Update UI state
      setImageFiles(prev => ({
        ...prev,
        [fieldName]: {
          file,
          uploading: false,
          url: URL.createObjectURL(file)
        }
      }));
    } catch (error) {
      console.error('Error uploading image:', error);
      setImageFiles(prev => ({
        ...prev,
        [fieldName]: {
          file,
          uploading: false,
          error: true
        }
      }));
    }
  };
  
  // Helper function to convert file to Base64
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        console.log("File converted to Base64");
        resolve(reader.result);
      };
      reader.onerror = error => reject(error);
    });
  };
  
  // Add this debugging log to verify formState before report generation
  useEffect(() => {
    if (formState?.qbCheckResultsImage) {
      console.log("Image data in formState:", 
        formState.qbCheckResultsImage.imageData ? 
        `Present (length: ${formState.qbCheckResultsImage.imageData.length})` : 
        "Missing");
    }
  }, [formState?.qbCheckResultsImage]);
  

  
  const breadcrumbTuples = [
    ["Patient Overview", `/patient/${patientId}`],
    ["Documents", `/patient/${patientId}/documents`],    
    [currentSectionTitle]
  ];

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />
      <div className="flex">       
        <div className="flex-grow p-8">

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={() => navigate(`/patient/${patientId}${prevSectionLink}`)}
              className="flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300"
              aria-label="Back to Previous Section"
            >
              <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
              <span className="font-bold">Back to {prevSectionTitle}</span>
            </button>

             <button
            onClick={handleFinish}
            className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition-all duration-300 ${isFormValid() ? 'hover:bg-violet-800 transition duration-100 bg-violet-900 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            disabled={!isFormValid()}
          >
                <span className="font-bold">{isSaving ? "Saving Progress..." : "Submit QbCheck Form"}</span>
                <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />
              </button>
            
          </div>


          {/* Section Title */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">{currentSectionNumber}. {currentSectionTitle}</h2>
          </div>

          {/* Subsections */}
          {Object.entries(subsections).map(([subsectionId, subsection]) => {
            if (!subsection) return null;
                        
            return (
              <div key={subsectionId} className="p-8 mb-8 bg-gray-50 rounded-2xl border border-gray-200">
                <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
                  {subsection.subsectionTitleDisplay}
                </h3>

                <div className="space-y-4">
                {Object.entries(subsection.questions || {}).map(([questionId, question], index, questionsArray) => {
    if (!question) return null;

                    return (
                      <React.Fragment key={questionId}>                        
                          <div className="mb-4 flex items-start">
                          <div className="flex flex-col w-96">
                              <h4 className="text-left mt-2 text-lg font-semibold text-gray-700">
                                {question.questionTitle}
                              </h4>
                              {question.tooltipText && (
                                <>
                                  <QuestionMarkCircleIcon
                                    className="w-4 h-4 text-violet-700 cursor-help mt-2"
                                    data-tooltip-id={`${question.firestoreFieldName}-tooltip`}
                                    data-tooltip-content={question.tooltipText}
                                  />
                                  <Tooltip
                                    id={`${question.firestoreFieldName}-tooltip`}
                                    place="top"
                                    style={{
                                      maxWidth: '300px',
                                      whiteSpace: 'normal',
                                      backgroundColor: 'rgba(34, 34, 34, 1)',
                                      borderRadius: '15px',
                                      color: '#fff',
                                      fontSize: '0.8em'
                                    }}
                                  />
                                </>
                              )}
                            {question.questionInstructionHeading && (
              <h5 className="text-left text-sm text-gray-700 font-semibold mt-1">{question.questionInstructionHeading}</h5>
            )}
            {question.questionInstructionText && (
              <p className="text-left text-xs text-gray-600 italic mt-1 whitespace-pre-line">{question.questionInstructionText}</p>
            )}
          </div>

                            <div className="ml-5">                              
                              
                            {subsection.subsectionType === 'verticalOptions' && (
  <div className="w-96 mt-2">
    <SelectionOptions
      question={question}
      formState={formState}
      handleSelect={handleSelect}
      handleOtherTextChange={handleOtherTextChange}
      handleOtherTextBlur={handleOtherTextBlur}
    />
  </div>
)}

{subsection.subsectionType === 'freeText' && (
  <div className="w-96 mt-2">
    {question.questionType === 'freeTextArea' ? (
      <>       
        <AutoResizingTextArea
          className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
          value={formState[question.firestoreFieldName]?.value || ''}
          onChange={(e) => handleFreeTextChange(
            question.firestoreFieldName, 
            e.target.value, 
            question.questionType
          )}
          onBlur={() => handleFreeTextBlur(
            question.firestoreFieldName,
            subsectionId,
            questionId
          )}
          placeholder="Enter your detailed response here..."
          rows={4}
        />
      </>
    ) : (
      <div className="relative">
        <input
          type="text"
          className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
          value={formState[question.firestoreFieldName]?.value || ''}
          onChange={(e) => handleFreeTextChange(
            question.firestoreFieldName, 
            e.target.value, 
            question.questionType
          )}
          onBlur={() => handleFreeTextBlur(
            question.firestoreFieldName,
            subsectionId,
            questionId
          )}
          placeholder={question.questionType === 'freeTextNumerical' ? "Enter a number" : "Enter a percentile (0-100)"}
        />      
      </div>
    )}
  </div>
)}

{subsection.subsectionType === 'date' && (
  <div className="w-96 mt-2">
    <input
      type="date"
      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
      value={formState[question.firestoreFieldName]?.value || ''}
      onChange={(e) => handleDateChange(question.firestoreFieldName, e.target.value)}
    />
  </div>
)}

{subsection.subsectionType === 'imageUpload' && (
  <div className="w-96 mt-2">
  <div 
    className={`
      border-2 border-dashed rounded-lg p-4 text-center cursor-pointer
      transition-all duration-200
      ${imageFiles[question.firestoreFieldName]?.url 
        ? 'border-green-500 bg-green-50' 
        : 'border-violet-300 hover:border-violet-500 bg-violet-50 hover:bg-violet-100'
      }
    `}
    onClick={() => fileInputRefs.current[question.firestoreFieldName].click()}
    onDragOver={(e) => {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.classList.add('border-violet-500', 'bg-violet-100');
    }}
    onDragEnter={(e) => {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.classList.add('border-violet-500', 'bg-violet-100');
    }}
    onDragLeave={(e) => {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.classList.remove('border-violet-500', 'bg-violet-100');
    }}
    onDrop={(e) => {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.classList.remove('border-violet-500', 'bg-violet-100');
      
      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        // Create a synthetic event object with the file
        const syntheticEvent = { target: { files: files } };
        handleImageUpload(
          syntheticEvent,
          question.firestoreFieldName,
          subsectionId,
          questionId
        );
      }
    }}
  >
    <input
      type="file"
      accept=".jpg,.jpeg,.png,.tiff,.webp"
      className="hidden"
      ref={el => fileInputRefs.current[question.firestoreFieldName] = el}
      onChange={(e) => handleImageUpload(
        e,
        question.firestoreFieldName,
        subsectionId,
        questionId
      )}
    />

      {!imageFiles[question.firestoreFieldName]?.url && !imageFiles[question.firestoreFieldName]?.uploading && (
        <div className="flex flex-col items-center">
          <svg className="w-10 h-10 text-violet-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
          <p className="text-sm text-gray-700">Click to upload or drag and drop</p>
          <p className="text-xs text-gray-500 mt-1">JPG, PNG, TIFF, WEBP</p>
        </div>
      )}

      {imageFiles[question.firestoreFieldName]?.uploading && (
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-700 mb-2"></div>
          <p className="text-sm text-violet-700">Uploading...</p>
        </div>
      )}

      {imageFiles[question.firestoreFieldName]?.url && (
        <div className="flex flex-col items-center">
          <svg className="w-10 h-10 text-green-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
          </svg>
          <p className="text-sm text-green-700">Image uploaded successfully</p>
          <p className="text-xs text-gray-500 mt-1">{imageFiles[question.firestoreFieldName]?.file?.name}</p>
          <button 
            className="mt-2 text-xs text-violet-700 hover:text-violet-900"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRefs.current[question.firestoreFieldName].click();
            }}
          >
            Replace image
          </button>
        </div>
      )}
    </div>
  </div>
)}

                            </div>
                          </div>
                        
                        {index < questionsArray.length - 1 && (
          <div className="border-t border-gray-200 my-4"></div>
        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {/**
           * Duplicate the primary Submit action at the bottom of the page so clinicians
           * don’t need to scroll back to the top after completing the form. This mirrors
           * the UX pattern used in Clinical Interview pages (e.g., CIPrefilledPage),
           * reducing friction and making the forward action always available.
           */}
          <div className="flex justify-end items-center mt-6">
            <button
              onClick={handleFinish}
              className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition-all duration-300 ${isFormValid() ? 'hover:bg-violet-800 transition duration-100 bg-violet-900 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              disabled={!isFormValid()}
            >
              <span className="font-bold">{isSaving ? "Saving Progress..." : "Submit QbCheck Form"}</span>
              <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Add Alert Dialog for finishing Post-Assessment Discussion */}
        <AlertDialog.Root open={isDialogOpen && !isGenerating} onOpenChange={setIsDialogOpen}>
          <AlertDialog.Portal>
            <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50" />
            <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[600px] max-w-[90vw] max-h-[85vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-4">
                <AlertDialog.Title className="text-xl font-semibold">
                  QbCheck Form Complete
                </AlertDialog.Title>
                <AlertDialog.Cancel asChild>
                  <button className="absolute top-2 right-3 text-gray-500 hover:text-gray-700">
                    <span aria-hidden>×</span>
                  </button>
                </AlertDialog.Cancel>
              </div>

              <div className="mb-6">
                <h3 className="font-semibold mb-2">Summary:</h3>
                <div className="text-gray-700">
                  The QbCheck Form has been completed. Would you like to generate this section of the report?
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <AlertDialog.Cancel asChild>
                  <button
                    onClick={() => setIsDialogOpen(false)}
                    className="py-2 px-4 mb-2 font-semibold border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </AlertDialog.Cancel>
                <AlertDialog.Action asChild>
                  <button
                    onClick={handleGenerateReport}
                    className="flex items-center px-4 py-2 mb-2 font-semibold rounded-md bg-violet-900 text-white hover:bg-violet-800"
                  >
                    <DocumentTextIcon className="w-5 h-5 mr-1" aria-hidden="true" />
                    Generate Report
                  </button>
                </AlertDialog.Action>
              </div>
            </AlertDialog.Content>
          </AlertDialog.Portal>
        </AlertDialog.Root>

        <GenerateReportProgressDialog
          reportStage="QbCheck"
          patientId={patientId}
          clinicianId={auth.currentUser?.uid || ''}
          handleNextPageButtonClick={handleNextPageButtonClick}
          isGenerating={isGenerating}
          onCancel={handleCancelGeneration}
        />
      </div>
    </Layout>
  );
}